import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate, getDaysToGo } from '../../utils/attendeeUtils';

const EventCard = ({ event, registration, showActions = true }) => {
  const imageUrl = event.image_url;
  const venue = event.venue_name || event.venue;
  const city = event.city || event.location;
  const daysToGo = getDaysToGo(event.start_date || event.start);
  const isUpcoming = event.status === 'upcoming' || event.status === 'ongoing' || new Date(event.start_date || event.start) >= new Date();
  const isCancelled = registration?.status === 'cancelled';
  const isPaid = registration?.payment_status === 'paid';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Event image */}
        {imageUrl && (
          <Link to={`/my-events/${event.id}`} className="sm:w-32 sm:flex-shrink-0">
            <img
              src={imageUrl}
              alt={event.title || event.event}
              className="w-full h-32 sm:h-full object-cover"
            />
          </Link>
        )}

        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-2">
            <Link to={`/my-events/${event.id}`} className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-white truncate hover:text-primary transition-colors">
                {event.title || event.event}
              </h3>
            </Link>
            {isUpcoming && (
              <span className="flex-shrink-0 px-2 py-0.5 text-xs font-bold rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                {daysToGo}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDate(event.start_date || event.start)}
            </span>
            {venue && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {venue}
              </span>
            )}
            {city && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                </svg>
                {city}
              </span>
            )}
          </div>

          {/* Status badges */}
          {registration && (
            <div className="flex items-center gap-2 mt-3">
              {isCancelled ? (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300">
                  Cancelled
                </span>
              ) : (
                <>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                    Registered
                  </span>
                  {isPaid ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                      Paid
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
                      Payment Pending
                    </span>
                  )}
                  {registration.checked_in_at && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-300">
                      Checked In
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Actions */}
          {showActions && (
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <Link
                to={`/my-events/${event.id}`}
                className="text-xs font-medium text-primary hover:text-primary-dark flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Open Event Hub
              </Link>
              {registration && !isCancelled && (
                <Link
                  to="/attendee/tickets"
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  View Ticket
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
